import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { searchProducts } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';
import { getArticleImageUrl } from '../utils/imageUtils';
import PriceDisplay from './PriceDisplay';

const ProductSearch = ({ onSearch, onClear, onSymptomSelect, placeholder }) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [matchedSymptom, setMatchedSymptom] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowPreview(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      setIsLoading(true);
      setShowPreview(true);

      const fetchResults = async () => {
        try {
          const response = await searchProducts(searchTerm, 1, 5);
          if (response?.success && Array.isArray(response.data.products)) {
            setPreviewResults(response.data.products);
            setMatchedSymptom(response.data.matchedSymptom || null);
          }
        } catch (error) {
          console.error('Search preview failed:', error);
          setPreviewResults([]);
          setMatchedSymptom(null);
        } finally {
          setIsLoading(false);
        }
      };

      const timer = setTimeout(fetchResults, 300);
      return () => clearTimeout(timer);
    } else {
      setPreviewResults([]);
      setMatchedSymptom(null);
      setShowPreview(false);
    }
  }, [searchTerm]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      onSearch?.(searchTerm.trim());
      setShowPreview(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setShowPreview(false);
    onClear?.();
  };

  const handlePreviewClick = (product) => {
    // When a product is clicked in the preview, search for it
    const searchQuery = product._id || product.pharmacode || product.productNumber;
    if (searchQuery) {
      onSearch?.(searchQuery);
      setShowPreview(false);
    }
  };

  const getOptimizedImageUrl = (images) => {
    if (!Array.isArray(images) || images.length === 0) return null;
    
    const picFront3D = images.find(image => image.includes('PICFRONT3D'));
    if (picFront3D) {
      return picFront3D;
    }
    
    const picFront = images.find(image => image.includes('PICFRONT'));
    if (picFront) {
      return picFront;
    }
    
    return images[0];
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-xl">
      <form onSubmit={handleSearchSubmit} className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
          <FaSearch className="h-5 w-5 text-indigo-500" />
        </div>
        <input
          type="text"
          placeholder={placeholder || t('filters.productSearch.placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-12 pr-12 py-3 rounded-xl border-2 border-gray-200 bg-white shadow-lg hover:shadow-xl focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 text-base transition-all duration-300 placeholder-gray-400 min-w-[180px]"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-indigo-500 transition-colors duration-200"
            tabIndex={-1}
          >
            <FaTimes className="h-5 w-5" />
          </button>
        )}
      </form>

      <AnimatePresence>
        {showPreview && searchTerm.trim().length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-2xl border-2 border-gray-200 max-h-96 overflow-y-auto animate-fade-in"
          >
            {matchedSymptom && !isLoading && (
              <button
                type="button"
                onClick={() => { onSymptomSelect?.(matchedSymptom.key, matchedSymptom.label); setShowPreview(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-cyan-50 to-blue-50 hover:from-cyan-100 hover:to-blue-100 border-b border-cyan-100 text-left transition-colors rounded-t-2xl"
              >
                <span className="text-xl">🩹</span>
                <span className="flex-1 text-sm">
                  <span className="font-semibold text-cyan-800">{matchedSymptom.label}</span>
                  <span className="text-cyan-700"> — {t('filters.productSearch.symptomShortcut')}</span>
                </span>
                <span className="text-cyan-600">→</span>
              </button>
            )}
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-indigo-400 border-r-transparent"></div>
                <p className="mt-2 text-base text-gray-500">{t('filters.productSearch.searching')}</p>
              </div>
            ) : previewResults.length > 0 ? (
              <motion.div 
                className="py-2 divide-y divide-gray-50"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.05
                    }
                  }
                }}
              >
                {previewResults.map((product) => {
                  const firstArticle = product.articles?.[0] || {};
                  const articleImages = Array.isArray(firstArticle.images)
                    ? { F: firstArticle.images, M: firstArticle.images }
                    : (firstArticle.images || (Array.isArray(product.images) ? { F: product.images } : (product.images || {})));
                  const article = {
                    pharmacode: firstArticle.pharmacode || product.pharmacode || product.productNumber,
                    description: {
                      description: firstArticle.description?.description || product.description?.description || t('filters.productSearch.noDescriptionAvailable'),
                      longDescription: firstArticle.description?.longDescription || product.description?.longDescription || product.longDescription || firstArticle.description?.description || t('filters.productSearch.noDescriptionAvailable')
                    },
                    hasPhoto: firstArticle.hasPhoto ?? product.hasPhoto ?? false,
                    images: articleImages,
                    price: firstArticle.price ?? null
                  };

                  return (
                    <motion.div
                      key={product._id}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: { opacity: 1, y: 0 }
                      }}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => handlePreviewClick(product)}
                      className="px-4 py-3 hover:bg-indigo-50 cursor-pointer flex items-center space-x-4 transition-colors duration-200 rounded-xl"
                    >
                      <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        {(() => {
                          const imgUrl = getArticleImageUrl(article, 'F');
                          if (imgUrl) {
                            return (
                              <img
                                src={imgUrl}
                                alt={article.description.description}
                                className="w-full h-full object-contain"
                              />
                            );
                          }
                          return (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-gray-900 line-clamp-2">
                          {article.description.longDescription}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {t('filters.productSearch.pharmacode')} {article.pharmacode}
                        </p>
                        <PriceDisplay
                          article={article}
                          fallbackKey="filters.productSearch.priceNotAvailable"
                          className="mt-1 text-indigo-600"
                        />
                      </div>
                    </motion.div>
                  );
                })}
                <div className="px-4 py-2 bg-indigo-50 text-center rounded-b-2xl">
                  <button
                    onClick={handleSearchSubmit}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    {t('filters.productSearch.viewAllResults', { searchTerm })}
                  </button>
                </div>
              </motion.div>
            ) : (
              <div className="p-8 text-center text-gray-400">
                <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="mt-2 text-base">{t('filters.productSearch.noProductsFound', { searchTerm })}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductSearch; 